import { useEffect, useState, useRef } from 'react';
import { format } from 'date-fns';
import { nl } from 'date-fns/locale';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { useToast } from '../../contexts/ToastContext';
import { t } from '../../i18n/nl.js';
import { sendPushToTokens, sendPushToClient } from '../../utils/pushUtils.js';
import { toAmsterdamTime, toAmsterdamDate } from '../../utils/dateUtils.js';
import { useAuth } from '../../contexts/AuthContext.jsx';
import { lesCoachNaam } from '../../utils/lesCoachUtils.js';

function calcEndDate(membership) {
  if (!membership) return null;
  if (membership.end_date) return membership.end_date;
  if (membership.initial_end_date) return membership.initial_end_date;
  if (membership.start_date && membership.initial_duration_months) {
    const d = new Date(membership.start_date + 'T00:00:00');
    d.setDate(d.getDate() + membership.initial_duration_months * 31);
    return d.toISOString().slice(0, 10);
  }
  return null;
}

export default function LesDetailModal({ les, enrollmentCount, onClose, onRefresh, prefillEnroll }) {
  const navigate = useNavigate();
  const { role } = useAuth();
  const { showSuccess, showError } = useToast();
  const backdropRef = useRef(null);
  const [enrollments, setEnrollments] = useState([]);
  const [loadingEnrollments, setLoadingEnrollments] = useState(true);
  const [errorEnrollments, setErrorEnrollments] = useState('');
  const [confirmUnenroll, setConfirmUnenroll] = useState(null);
  const [unenrolling, setUnenrolling] = useState(false);
  const [showCancelForm, setShowCancelForm] = useState(false);
  const [cancelScope, setCancelScope] = useState('single');
  const [cancelReason, setCancelReason] = useState('');
  const [cancelling, setCancelling] = useState(false);

  // Coach naam (voor standaard uitschrijf-pushbericht en inschrijf-push)
  const [coachNaam, setCoachNaam] = useState('');
  const [coachId, setCoachId] = useState('');

  // Uitschrijf-push state
  const [unenrollPushChecked, setUnenrollPushChecked] = useState(true);
  const [unenrollPushBericht, setUnenrollPushBericht] = useState('');

  // Push formulier state
  const [showPushForm, setShowPushForm] = useState(false);
  const [pushOntvangers, setPushOntvangers] = useState('enrolled');
  const [pushBericht, setPushBericht] = useState('');
  const [pushSending, setPushSending] = useState(false);
  const [pushProgress, setPushProgress] = useState(null);
  const [pushDone, setPushDone] = useState(null);
  const [pushError, setPushError] = useState('');

  // Coach inschrijfformulier state
  const [showEnrollForm, setShowEnrollForm] = useState(false);
  const [availableClients, setAvailableClients] = useState([]);
  const [loadingClients, setLoadingClients] = useState(false);
  const [selectedClientId, setSelectedClientId] = useState('');
  const [requireConfirmation, setRequireConfirmation] = useState(false);
  const [chargeCredits, setChargeCredits] = useState(les.credit_cost > 0);
  const [isTrial, setIsTrial] = useState(false);
  const [enrolling, setEnrolling] = useState(false);
  const [enrollError, setEnrollError] = useState('');

  // Hover-kaart state
  const hoverCacheRef = useRef({});
  const [hoveredCard, setHoveredCard] = useState(null);
  const hideTimerRef = useRef(null);

  const isCoachRole = role && ['superadmin', 'admin_coach', 'coach'].includes(role);

  useEffect(() => { loadEnrollments(); }, [les.id]);

  useEffect(() => {
    const channel = supabase
      .channel(`lesson-enrollments-${les.id}`)
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'lesson_enrollments',
        filter: `lesson_id=eq.${les.id}`,
      }, () => {
        loadEnrollments();
      })
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [les.id]);

  useEffect(() => {
    async function fetchCoachNaam() {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return;
        setCoachId(user.id);
        const { data } = await supabase
          .from('profiles')
          .select('full_name')
          .eq('id', user.id)
          .maybeSingle();
        if (data?.full_name) setCoachNaam(data.full_name);
      } catch (err) {
        // Alleen de weergavenaam in de push-standaardteksten; er is een
        // fallback (`unenrollCoachFallback`), dus geen UI-melding. Wel loggen:
        // faalt dit door een sessieprobleem, dan blijft ook `coachId` leeg en
        // wordt élke push uit dit scherm stil overgeslagen.
        console.error('Fout bij ophalen coachnaam voor pushteksten:', err);
      }
    }
    fetchCoachNaam();
  }, []);

  async function loadEnrollments() {
    setLoadingEnrollments(true);
    setErrorEnrollments('');
    try {
      const { data, error } = await supabase
        .from('lesson_enrollments')
        .select('id, client_id, status, enrolled_at, profiles!lesson_enrollments_client_id_fkey(id, full_name, customer_number)')
        .eq('lesson_id', les.id)
        .in('status', ['enrolled', 'waitlisted', 'pending_confirmation'])
        .order('enrolled_at', { ascending: true });
      if (error) throw error;
      setEnrollments(data || []);
    } catch (err) {
      setErrorEnrollments(t('lesDetail.foutLaden') + err.message);
    } finally {
      setLoadingEnrollments(false);
    }
  }

  async function loadAvailableClients() {
    setLoadingClients(true);
    setEnrollError('');
    try {
      const { data: allClients, error: err1 } = await supabase
        .from('profiles')
        .select('id, full_name, customer_number')
        .eq('role', 'client')
        .order('full_name', { ascending: true });
      if (err1) throw err1;

      const { data: enrolled, error: err2 } = await supabase
        .from('lesson_enrollments')
        .select('client_id')
        .eq('lesson_id', les.id)
        .in('status', ['enrolled', 'pending_confirmation', 'waitlisted']);
      if (err2) throw err2;

      const enrolledIds = new Set((enrolled || []).map(e => e.client_id));
      // BUG 12: Ook de vaste PT-client uitsluiten (is al ingeschreven via lesson_enrollments)
      const ptClientId = les.client_id ?? null;
      setAvailableClients((allClients || []).filter(c => !enrolledIds.has(c.id) && c.id !== ptClientId));
    } catch (err) {
      setEnrollError(t('lesDetail.inschrijfFout') + err.message);
    } finally {
      setLoadingClients(false);
    }
  }

  async function handleCoachEnroll() {
    if (!selectedClientId) return;
    setEnrolling(true);
    setEnrollError('');
    try {
      const { data, error } = await supabase.rpc('coach_enroll_client', {
        p_lesson_id: les.id,
        p_client_id: selectedClientId,
        p_require_confirmation: requireConfirmation,
        p_charge_credits: chargeCredits,
        p_is_trial: isTrial,
      });
      if (error) throw error;

      const status = data?.status;
      const client = availableClients.find(c => c.id === selectedClientId);
      const naam = client?.full_name || '?';
      const datum = format(toAmsterdamDate(les.starts_at), 'EEEE d MMMM', { locale: nl });
      const tijd = toAmsterdamTime(les.starts_at);

      let toastMsg = '';
      let pushBody = '';
      if (status === 'enrolled') {
        toastMsg = t('lesDetail.inschrijfSuccesEnrolled');
        pushBody = t('lesDetail.inschrijfPushEnrolled')
          .replace('{les}', les.name)
          .replace('{datum}', datum)
          .replace('{tijd}', tijd)
          .replace('{coach}', coachNaam || t('lesDetail.unenrollCoachFallback'));
      } else if (status === 'pending_confirmation') {
        toastMsg = t('lesDetail.inschrijfSuccesPending');
        pushBody = t('lesDetail.inschrijfPushPending')
          .replace('{les}', les.name)
          .replace('{datum}', datum)
          .replace('{tijd}', tijd);
      } else {
        toastMsg = t('lesDetail.inschrijfSuccesWaitlisted');
        pushBody = t('lesDetail.inschrijfPushWaitlisted')
          .replace('{les}', les.name)
          .replace('{datum}', datum)
          .replace('{tijd}', tijd);
      }

      // Stuur push naar client
      try {
        if (coachId) {
          await sendPushToClient(
            supabase,
            coachId,
            selectedClientId,
            'lessons_schedule',
            les.name,
            pushBody,
          );
        }
      } catch (err) {
        // Best-effort: de inschrijving is al gedaan en mag niet als mislukt
        // worden gemeld. Deze push gaat automatisch mee (de coach vraagt er
        // niet expliciet om), dus alleen loggen — geen UI-melding.
        console.error('Fout bij versturen inschrijfpush naar cliënt:', err);
      }

      showSuccess(toastMsg);
      setShowEnrollForm(false);
      setSelectedClientId('');
      setRequireConfirmation(false);
      setChargeCredits(les.credit_cost > 0);
      setIsTrial(false);
      await loadEnrollments();
      onRefresh();
    } catch (err) {
      setEnrollError(t('lesDetail.inschrijfFout') + err.message);
    } finally {
      setEnrolling(false);
    }
  }

  async function handleCancelPending(enrollment) {
    try {
      const { error } = await supabase
        .from('lesson_enrollments')
        .delete()
        .eq('id', enrollment.id)
        .eq('status', 'pending_confirmation');
      if (error) throw error;
      const naam = enrollment.profiles?.full_name || '?';
      showSuccess(t('lesDetail.pendingAnnulerenSucces').replace('{naam}', naam));
      await loadEnrollments();
      onRefresh();
    } catch (err) {
      showError(t('lesDetail.pendingAnnulerenFout') + err.message);
    }
  }

  function handleAskConfirm(enrollment) {
    setConfirmUnenroll(enrollment);
    setUnenrollPushChecked(true);
    const datum = format(toAmsterdamDate(les.starts_at), 'EEEE d MMMM', { locale: nl });
    const tijd = toAmsterdamTime(les.starts_at);
    setUnenrollPushBericht(
      t('lesDetail.unenrollDefaultBericht')
        .replace('{coach}', coachNaam || t('lesDetail.unenrollCoachFallback'))
        .replace('{les}', les.name)
        .replace('{datum}', datum)
        .replace('{tijd}', tijd)
    );
  }

  async function handleUnenroll(enrollment) {
    setUnenrolling(true);
    let pushMislukt = false;
    try {
      const { data, error } = await supabase.rpc('coach_unenroll_client', {
        p_lesson_id: les.id,
        p_client_id: enrollment.client_id,
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);

      if (unenrollPushChecked && unenrollPushBericht.trim()) {
        try {
          const { data: { user } } = await supabase.auth.getUser();
          await sendPushToClient(
            supabase,
            user.id,
            enrollment.client_id,
            'lessons_schedule',
            les.name,
            unenrollPushBericht.trim()
          );
        } catch (err) {
          // Enige van de drie pushblokken hier waar de coach het bericht zelf
          // heeft aangevinkt en ingetypt: hij gaat er dus van uit dat de
          // cliënt op de hoogte is. Uitschrijven zelf is al geslaagd (dus geen
          // throw), maar de mislukte melding moet wél zichtbaar zijn.
          console.error('Fout bij versturen uitschrijfpush naar cliënt:', err);
          pushMislukt = true;
        }
      }

      const naam = enrollment.profiles?.full_name || '?';
      if (pushMislukt) {
        showError(t('lesDetail.uitschrijvenSuccesPushFout').replace('{naam}', naam));
      } else {
        showSuccess(t('lesDetail.uitschrijvenSucces').replace('{naam}', naam));
      }
      setConfirmUnenroll(null);
      await loadEnrollments();
      onRefresh();
    } catch (err) {
      showError(t('lesDetail.uitschrijvenFout') + err.message);
    } finally {
      setUnenrolling(false);
    }
  }

  async function sendCancelPushToCoaches() {
    const targetIds = new Set();
    if (les.coach_id && les.coach_id !== coachId) targetIds.add(les.coach_id);
    if (les.executing_coach_id && les.executing_coach_id !== coachId) targetIds.add(les.executing_coach_id);
    if (targetIds.size === 0) return;

    const datumStr = new Date(les.starts_at).toLocaleDateString('nl-NL', {
      timeZone: 'Europe/Amsterdam', weekday: 'short', day: 'numeric', month: 'long',
    });

    for (const targetId of targetIds) {
      try {
        await sendPushToClient(
          supabase, coachId, targetId, 'lessons_schedule',
          t('coachPush.lesGeannuleerd'),
          t('coachPush.lesGeannuleerdBody').replace('{naam}', les.name).replace('{datum}', datumStr),
        );
      } catch (err) {
        // Best-effort per ontvanger: deze functie draait midden in
        // handleCancel, ná de geslaagde cancel_lesson_with_refund-RPC. Een
        // throw zou de annulering ten onrechte als mislukt tonen én de tweede
        // coach overslaan. Automatische melding, dus alleen loggen.
        console.error(`Fout bij versturen annuleringspush naar ${targetId}:`, err);
      }
    }
  }

  async function handleCancel() {
    setCancelling(true);
    try {
      let idsToCancel = [les.id];

      if (les.recurrence_id && cancelScope !== 'single') {
        // Zelfde ondergrens als bewerkscope "alle": alleen toekomstige, nog niet
        // gegeven lessen — voorkomt dat annuleren met terugwerkende kracht historie
        // raakt terwijl credit-afschrijvingen blijven staan.
        const lowerBound = cancelScope === 'from_here' ? les.starts_at : new Date().toISOString();
        const query = supabase
          .from('group_lessons')
          .select('id')
          .eq('recurrence_id', les.recurrence_id)
          .eq('is_cancelled', false)
          .gte('starts_at', lowerBound);
        const { data, error } = await query;
        if (error) throw error;
        idsToCancel = (data || []).map(l => l.id);
        if (!idsToCancel.includes(les.id)) idsToCancel.push(les.id);
      }

      // Onderdeel 3 (2026-07-10): atomaire RPC i.p.v. kale is_cancelled=true-
      // update — boekt per inschrijving het afgeschreven credit terug naar
      // de juiste bron (pool/legacy) en logt een credit_transactions-regel.
      const results = await Promise.all(
        idsToCancel.map(id => supabase.rpc('cancel_lesson_with_refund', {
          p_lesson_id: id,
          p_reason: cancelReason.trim() || null,
        })),
      );
      const failed = results.find(r => r.error);
      if (failed) throw failed.error;
      const rpcError = results.find(r => r.data?.error);
      if (rpcError) throw new Error(rpcError.data.error);

      // Push naar coach(es) die niet zelf hebben geannuleerd
      await sendCancelPushToCoaches();

      showSuccess(t('lesDetail.cancelSucces').replace('{count}', idsToCancel.length));
      setShowCancelForm(false);
      setCancelReason('');
      setCancelScope('single');
      onRefresh();
      setTimeout(() => onClose(), 2000);
    } catch (err) {
      showError(t('lesDetail.cancelFout') + err.message);
    } finally {
      setCancelling(false);
    }
  }

  async function handleSendPush() {
    setPushError('');
    if (!pushBericht.trim()) { setPushError(t('lesDetail.pushBerichtVerplicht')); return; }

    let targets = [];
    if (pushOntvangers === 'enrolled') targets = enrolledList;
    else if (pushOntvangers === 'waitlisted') targets = waitlistedList;
    else targets = [...enrolledList, ...waitlistedList];

    if (targets.length === 0) { setPushError(t('lesDetail.pushGeenOntvangers')); return; }

    const clientIds = targets.map(e => e.client_id);

    setPushSending(true);
    setPushProgress({ sent: 0, total: clientIds.length, skipped: 0 });

    try {
      const { data: { user } } = await supabase.auth.getUser();
      const { successCount } = await sendPushToTokens(
        supabase, user.id, clientIds, 'lessons_schedule', les.name, pushBericht.trim()
      );
      setPushDone({ sent: successCount, skipped: clientIds.length - successCount });
    } catch (err) {
      setPushError(t('lesDetail.pushFoutTokens') + err.message);
    } finally {
      setPushSending(false);
      setPushProgress(null);
    }
  }

  function openPushForm() {
    setShowPushForm(s => {
      if (s) {
        setPushBericht('');
        setPushProgress(null);
        setPushDone(null);
        setPushError('');
      }
      return !s;
    });
  }

  // Hover-kaart handlers
  function scheduleHide() {
    hideTimerRef.current = setTimeout(() => setHoveredCard(null), 150);
  }

  function cancelHide() {
    clearTimeout(hideTimerRef.current);
  }

  async function handleRowEnter(enrollment, rect) {
    cancelHide();
    const clientId = enrollment.client_id;
    const cardWidth = 240;
    const spaceRight = window.innerWidth - rect.right;
    const x = spaceRight >= cardWidth + 12 ? rect.right + 8 : rect.left - cardWidth - 8;
    const y = Math.min(Math.max(rect.top, 8), window.innerHeight - 300);

    const cached = hoverCacheRef.current[clientId];
    setHoveredCard({ clientId, x, y, enrollment, loading: cached === undefined });

    if (cached !== undefined) return;

    hoverCacheRef.current[clientId] = 'loading';
    const today = new Date().toISOString().slice(0, 10);
    const [{ data: membershipData }, { count: collectionsCount }] = await Promise.all([
      supabase
        .from('client_memberships')
        .select('start_date, end_date, initial_end_date, initial_duration_months, membership_types(name)')
        .eq('client_id', clientId)
        .eq('status', 'active')
        .order('start_date', { ascending: false })
        .order('created_at', { ascending: false })
        .order('id', { ascending: false })
        .limit(1)
        .maybeSingle(),
      supabase
        .from('invoices')
        .select('id', { count: 'exact', head: true })
        .eq('client_id', clientId)
        .eq('status', 'collections'),
    ]);

    hoverCacheRef.current[clientId] = {
      membership: membershipData || null,
      has_collections: (collectionsCount ?? 0) > 0,
    };
    setHoveredCard(prev =>
      prev?.clientId === clientId ? { ...prev, loading: false } : prev
    );
  }

  function handleRowLeave() {
    scheduleHide();
  }

  function toggleEnrollForm() {
    if (!showEnrollForm) {
      setShowEnrollForm(true);
      setSelectedClientId(prefillEnroll?.clientId || '');
      setRequireConfirmation(false);
      setChargeCredits(prefillEnroll?.isTrial ? false : les.credit_cost > 0);
      setIsTrial(!!prefillEnroll?.isTrial);
      setEnrollError('');
      loadAvailableClients();
    } else {
      setShowEnrollForm(false);
    }
  }

  const enrolledList = enrollments.filter(e => e.status === 'enrolled');
  const waitlistedList = enrollments.filter(e => e.status === 'waitlisted');
  const pendingList = enrollments.filter(e => e.status === 'pending_confirmation');
  const lesDate = toAmsterdamDate(les.starts_at);
  const lesType = les.type === 'personal' ? t('lesDetail.typePT') : les.type === 'workshop' ? t('lesDetail.typeWorkshop') : t('lesDetail.typeGroepsles');
  const isRecurring = !!les.recurrence_id;
  const isCancelled = les.is_cancelled;
  const enrolled = enrollmentCount ?? enrolledList.length;

  function handleBackdropClick(e) {
    if (e.target === backdropRef.current) onClose();
  }

  return (
    <div
      ref={backdropRef}
      onClick={handleBackdropClick}
      style={{
        position: 'fixed', inset: 0, zIndex: 1000,
        background: 'rgba(0,0,0,0.5)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 24,
      }}
    >
      <div style={{
        background: 'var(--color-surface)',
        borderRadius: 'var(--radius)',
        width: '100%', maxWidth: 640,
        maxHeight: '90vh',
        display: 'flex', flexDirection: 'column',
        boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
      }}>
        {/* Header */}
        <div style={{
          display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
          padding: '20px 20px 16px',
          borderBottom: '1px solid var(--color-border)',
          flexShrink: 0,
        }}>
          <div style={{ flex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: 'var(--color-text)' }}>
                {les.name}
              </h2>
              {isRecurring && (
                <span style={{
                  background: 'var(--color-primary)', color: '#fff',
                  fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 4,
                }}>
                  REEKS
                </span>
              )}
              {isCancelled && (
                <span style={{
                  background: 'var(--color-danger)', color: '#fff',
                  fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 4,
                }}>
                  {t('rooster.geannuleerd')}
                </span>
              )}
            </div>
            <div style={{ fontSize: 13, color: 'var(--color-text-muted)', marginTop: 4 }}>
              {format(lesDate, 'EEEE d MMMM yyyy', { locale: nl })} · {toAmsterdamTime(les.starts_at)} · {les.duration_minutes} min
            </div>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'none', border: 'none', cursor: 'pointer',
              fontSize: 22, color: 'var(--color-text-muted)',
              padding: '0 4px', lineHeight: 1, flexShrink: 0,
            }}
            aria-label={t('lesDetail.sluiten')}
          >
            ×
          </button>
        </div>

        {/* Scrollable content */}
        <div style={{ overflow: 'auto', flex: 1, padding: '16px 20px 20px' }}>
          {/* Lesinformatie */}
          <div style={{
            display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px 16px',
            marginBottom: 16, fontSize: 13,
          }}>
            {les.locations?.name && <InfoRow label="📍" value={les.locations.name} />}
            <InfoRow label="🏷" value={lesType} />
            {!!lesCoachNaam(les) && <InfoRow label="👤" value={lesCoachNaam(les)} />}
            <InfoRow
              label="👥"
              value={`${enrolled}/${les.max_participants} ${t('lesDetail.bezetting')}`}
              danger={enrolled >= les.max_participants}
            />
          </div>

          {les.description && (
            <div style={{
              padding: '10px 12px', background: 'var(--color-bg)',
              borderRadius: 6, fontSize: 13, color: 'var(--color-text-muted)',
              marginBottom: 16, fontStyle: 'italic',
            }}>
              {les.description}
            </div>
          )}

          {errorEnrollments && (
            <div className="error-msg" style={{ marginBottom: 12 }}>{errorEnrollments}</div>
          )}

          {loadingEnrollments ? (
            <div className="loading-center" style={{ padding: '24px 0' }}>
              <div className="spinner" />
            </div>
          ) : (
            <>
              {/* Ingeschreven */}
              <SectionHeader label={t('lesDetail.ingeschrevenen')} count={enrolledList.length} />
              {enrolledList.length === 0 ? (
                <EmptyRow text={t('lesDetail.geenIngeschrevenen')} />
              ) : (
                <div style={{ marginBottom: 16 }}>
                  {enrolledList.map(e => (
                    <EnrolledRow
                      key={e.id}
                      enrollment={e}
                      confirmId={confirmUnenroll?.id}
                      unenrolling={unenrolling}
                      onAskConfirm={handleAskConfirm}
                      onConfirm={handleUnenroll}
                      onCancel={() => setConfirmUnenroll(null)}
                      onRowEnter={handleRowEnter}
                      onRowLeave={handleRowLeave}
                      pushChecked={unenrollPushChecked}
                      onPushCheckedChange={setUnenrollPushChecked}
                      pushBericht={unenrollPushBericht}
                      onPushBerichtChange={setUnenrollPushBericht}
                    />
                  ))}
                </div>
              )}

              {/* Wachtlijst */}
              <SectionHeader label={t('lesDetail.wachtlijst')} count={waitlistedList.length} />
              {waitlistedList.length === 0 ? (
                <EmptyRow text={t('lesDetail.geenWachtlijst')} />
              ) : (
                <div style={{ marginBottom: 16 }}>
                  {waitlistedList.map((e, idx) => (
                    <WaitlistRow
                      key={e.id}
                      enrollment={e}
                      position={idx + 1}
                      onRowEnter={handleRowEnter}
                      onRowLeave={handleRowLeave}
                    />
                  ))}
                </div>
              )}

              {/* Wacht op bevestiging */}
              <SectionHeader label={t('lesDetail.pendingTitel')} count={pendingList.length} color="#d97706" />
              {pendingList.length === 0 ? (
                <EmptyRow text={t('lesDetail.geenPending')} />
              ) : (
                <div style={{ marginBottom: 16 }}>
                  {pendingList.map(e => (
                    <PendingRow
                      key={e.id}
                      enrollment={e}
                      onCancel={handleCancelPending}
                      onRowEnter={handleRowEnter}
                      onRowLeave={handleRowLeave}
                    />
                  ))}
                </div>
              )}

              {/* Coach inschrijfformulier */}
              {showEnrollForm && (
                <div style={{
                  border: '1px solid var(--color-primary)', borderRadius: 8,
                  padding: 16, marginTop: 8,
                  background: 'rgba(29,158,117,0.04)',
                }}>
                  <div style={{ fontWeight: 700, color: 'var(--color-primary)', marginBottom: 12 }}>
                    {t('lesDetail.inschrijfFormTitel')}
                  </div>
                  <div style={{ marginBottom: 12 }}>
                    <label style={{ display: 'block', fontSize: 12, color: 'var(--color-text-muted)', marginBottom: 4, fontWeight: 500 }}>
                      {t('lesDetail.inschrijfClientLabel')}
                    </label>
                    {loadingClients ? (
                      <div style={{ fontSize: 13, color: 'var(--color-text-muted)' }}>{t('lesDetail.inschrijfLaden')}</div>
                    ) : availableClients.length === 0 ? (
                      <div style={{ fontSize: 13, color: 'var(--color-text-muted)' }}>{t('lesDetail.inschrijfGeenClients')}</div>
                    ) : (
                      <select
                        className="form-input"
                        value={selectedClientId}
                        onChange={e => setSelectedClientId(e.target.value)}
                        disabled={enrolling}
                        style={{ maxWidth: '100%' }}
                      >
                        <option value="">{t('lesDetail.inschrijfClientPlaceholder')}</option>
                        {availableClients.map(c => (
                          <option key={c.id} value={c.id}>
                            {c.full_name}{c.customer_number ? ` (${c.customer_number})` : ''}
                          </option>
                        ))}
                      </select>
                    )}
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 12 }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13, userSelect: 'none' }}>
                      <input
                        type="checkbox"
                        checked={requireConfirmation}
                        onChange={e => setRequireConfirmation(e.target.checked)}
                        disabled={enrolling}
                        style={{ accentColor: 'var(--color-primary)', cursor: 'pointer' }}
                      />
                      {t('lesDetail.inschrijfBevestigingLabel')}
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: les.credit_cost > 0 ? 'pointer' : 'default', fontSize: 13, userSelect: 'none', opacity: les.credit_cost > 0 ? 1 : 0.5 }}>
                      <input
                        type="checkbox"
                        checked={chargeCredits}
                        onChange={e => setChargeCredits(e.target.checked)}
                        disabled={enrolling || les.credit_cost === 0 || isTrial}
                        style={{ accentColor: 'var(--color-primary)', cursor: les.credit_cost > 0 ? 'pointer' : 'default' }}
                      />
                      {t('lesDetail.inschrijfCreditsLabel')}
                    </label>
                    <div style={{ fontSize: 11, color: 'var(--color-text-muted)', paddingLeft: 24 }}>
                      {t('lesDetail.inschrijfCreditInfo').replace('{cost}', les.credit_cost ?? 0)}
                    </div>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13, userSelect: 'none', marginTop: 8 }}>
                      <input
                        type="checkbox"
                        checked={isTrial}
                        onChange={e => {
                          setIsTrial(e.target.checked);
                          if (e.target.checked) {
                            setChargeCredits(false);
                            setRequireConfirmation(false);
                          }
                        }}
                        disabled={enrolling}
                        style={{ accentColor: 'var(--color-primary)', cursor: 'pointer' }}
                      />
                      {t('trialSession.markAsTrial')}
                    </label>
                    {isTrial && (
                      <div style={{ fontSize: 11, color: 'var(--color-success)', paddingLeft: 24 }}>
                        {t('trialSession.trialExplainer')}
                      </div>
                    )}
                  </div>
                  {enrollError && (
                    <div className="error-msg" style={{ marginBottom: 10 }}>{enrollError}</div>
                  )}
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button
                      className="btn btn-ghost"
                      onClick={() => setShowEnrollForm(false)}
                      disabled={enrolling}
                      style={{ flex: 1 }}
                    >
                      {t('clientDetail.annuleren')}
                    </button>
                    <button
                      className="btn btn-primary"
                      onClick={handleCoachEnroll}
                      disabled={enrolling || !selectedClientId}
                      style={{ flex: 1 }}
                    >
                      {enrolling ? '…' : t('lesDetail.inschrijfOpslaan')}
                    </button>
                  </div>
                </div>
              )}
            </>
          )}

          {/* Pushbericht formulier */}
          {showPushForm && (
            <div style={{
              border: '1px solid var(--color-primary)', borderRadius: 8,
              padding: 16, marginTop: 16,
              background: 'rgba(29,158,117,0.04)',
            }}>
              <div style={{ fontWeight: 700, color: 'var(--color-primary)', marginBottom: 12 }}>
                {t('lesDetail.pushFormTitel')}
              </div>
              {!pushDone ? (
                <>
                  <div style={{ marginBottom: 12 }}>
                    <label style={{ display: 'block', fontSize: 12, color: 'var(--color-text-muted)', marginBottom: 4, fontWeight: 500 }}>
                      {t('lesDetail.pushOntvangersLabel')}
                    </label>
                    <select
                      className="form-input"
                      value={pushOntvangers}
                      onChange={e => setPushOntvangers(e.target.value)}
                      disabled={pushSending}
                      style={{ maxWidth: 320 }}
                    >
                      <option value="enrolled">{t('lesDetail.pushOntvangersEnrolled')}</option>
                      <option value="waitlisted">{t('lesDetail.pushOntvangersWaitlisted')}</option>
                      <option value="both">{t('lesDetail.pushOntvangersBoth')}</option>
                    </select>
                  </div>
                  <div style={{ marginBottom: 12 }}>
                    <label style={{ display: 'block', fontSize: 12, color: 'var(--color-text-muted)', marginBottom: 4, fontWeight: 500 }}>
                      {t('lesDetail.pushBerichtLabel')}
                    </label>
                    <textarea
                      className="form-input"
                      value={pushBericht}
                      onChange={e => setPushBericht(e.target.value)}
                      placeholder={t('lesDetail.pushBerichtPlaceholder')}
                      rows={3}
                      disabled={pushSending}
                      style={{ resize: 'vertical', fontFamily: 'inherit' }}
                    />
                  </div>
                  {pushError && (
                    <div className="error-msg" style={{ marginBottom: 10 }}>{pushError}</div>
                  )}
                  {pushProgress && (
                    <div style={{ marginBottom: 12 }}>
                      <div style={{
                        background: 'var(--color-border)', borderRadius: 4,
                        height: 8, overflow: 'hidden', marginBottom: 6,
                      }}>
                        <div style={{
                          background: 'var(--color-primary)', height: '100%',
                          width: `${pushProgress.total > 0
                            ? ((pushProgress.sent + pushProgress.skipped) / pushProgress.total) * 100
                            : 0}%`,
                          transition: 'width 0.2s',
                        }} />
                      </div>
                      <div style={{ fontSize: 12, color: 'var(--color-text-muted)' }}>
                        {t('lesDetail.pushVerzenden')
                          .replace('{sent}', pushProgress.sent)
                          .replace('{total}', pushProgress.total)}
                      </div>
                    </div>
                  )}
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button
                      className="btn btn-ghost"
                      onClick={() => {
                        setShowPushForm(false);
                        setPushBericht('');
                        setPushProgress(null);
                        setPushError('');
                      }}
                      disabled={pushSending}
                      style={{ flex: 1 }}
                    >
                      {t('clientDetail.annuleren')}
                    </button>
                    <button
                      className="btn btn-primary"
                      onClick={handleSendPush}
                      disabled={pushSending || !pushBericht.trim()}
                      style={{ flex: 1 }}
                    >
                      {pushSending ? '…' : t('lesDetail.pushVersturen')}
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <div style={{
                    fontSize: 14, color: '#2e7d32', marginBottom: 12,
                    fontWeight: 500, lineHeight: 1.5,
                  }}>
                    {t('lesDetail.pushSucces')
                      .replace('{sent}', pushDone.sent)
                      .replace('{skipped}', pushDone.skipped)}
                  </div>
                  <button
                    className="btn btn-ghost"
                    onClick={() => {
                      setShowPushForm(false);
                      setPushDone(null);
                      setPushBericht('');
                      setPushProgress(null);
                    }}
                    style={{ width: '100%' }}
                  >
                    {t('lesDetail.pushSluiten')}
                  </button>
                </>
              )}
            </div>
          )}

          {/* Annuleringsformulier */}
          {showCancelForm && !isCancelled && (
            <div style={{
              border: '1px solid var(--color-danger)', borderRadius: 8,
              padding: 16, marginTop: 16,
              background: 'rgba(226,75,74,0.04)',
            }}>
              <div style={{ fontWeight: 700, color: 'var(--color-danger)', marginBottom: 12 }}>
                {t('lesDetail.cancelFormTitel')}
              </div>
              {isRecurring && (
                <div style={{ marginBottom: 12 }}>
                  <div style={{ fontSize: 12, color: 'var(--color-text-muted)', marginBottom: 8 }}>
                    {t('lesDetail.cancelScopeLabel')}
                  </div>
                  {[
                    { value: 'single', label: t('lesDetail.cancelScopeSingle') },
                    { value: 'from_here', label: t('lesDetail.cancelScopeFromHere') },
                    { value: 'all', label: t('lesDetail.cancelScopeAll') },
                  ].map(opt => (
                    <label key={opt.value} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, cursor: 'pointer', fontSize: 13 }}>
                      <input
                        type="radio"
                        name="cancelScope"
                        value={opt.value}
                        checked={cancelScope === opt.value}
                        onChange={() => setCancelScope(opt.value)}
                        style={{ accentColor: 'var(--color-danger)', cursor: 'pointer' }}
                      />
                      {opt.label}
                    </label>
                  ))}
                </div>
              )}
              <div style={{ fontSize: 12, color: 'var(--color-text-muted)', marginBottom: 4 }}>
                {t('lesDetail.cancelRedenLabel')}
              </div>
              <textarea
                value={cancelReason}
                onChange={e => setCancelReason(e.target.value)}
                placeholder={t('lesDetail.cancelRedenPlaceholder')}
                rows={2}
                className="form-input"
                style={{ resize: 'vertical', fontFamily: 'inherit', marginBottom: 12 }}
              />
              <div style={{ display: 'flex', gap: 8 }}>
                <button
                  className="btn btn-ghost"
                  onClick={() => { setShowCancelForm(false); setCancelScope('single'); setCancelReason(''); }}
                  disabled={cancelling}
                  style={{ flex: 1 }}
                >
                  {t('clientDetail.annuleren')}
                </button>
                <button
                  onClick={handleCancel}
                  disabled={cancelling}
                  style={{
                    flex: 1, padding: '8px 16px', borderRadius: 6,
                    background: 'var(--color-danger)', border: 'none', color: '#fff',
                    fontWeight: 600, cursor: 'pointer', fontSize: 14,
                  }}
                >
                  {cancelling ? '…' : t('lesDetail.cancelBevestigen')}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{
          display: 'flex', gap: 8, flexWrap: 'wrap',
          padding: '12px 20px',
          borderTop: '1px solid var(--color-border)',
          flexShrink: 0,
        }}>
          {!isCancelled && (
            <>
              <button
                className="btn btn-ghost"
                onClick={() => navigate(`/coach/les/${les.id}/bewerken`)}
                style={{ flex: 1 }}
              >
                ✏️ {t('lesDetail.bewerken')}
              </button>
              <button
                className="btn btn-ghost"
                onClick={() => setShowCancelForm(s => !s)}
                style={{ flex: 1, color: 'var(--color-danger)', borderColor: 'var(--color-danger)' }}
              >
                ✕ {t('lesDetail.annuleren')}
              </button>
            </>
          )}
          {!isCancelled && isCoachRole && (
            <button
              className="btn btn-ghost"
              onClick={toggleEnrollForm}
              style={{ flex: 1, color: 'var(--color-primary)', borderColor: 'var(--color-primary)' }}
            >
              {t('lesDetail.clientInschrijvenKnop')}
            </button>
          )}
          <button
            className="btn btn-ghost"
            onClick={openPushForm}
            style={{ flex: 1 }}
          >
            🔔 {t('lesDetail.pushKnop')}
          </button>
          <button className="btn btn-ghost" onClick={onClose} style={{ flex: isCancelled ? 1 : undefined }}>
            {t('lesDetail.sluiten')}
          </button>
        </div>
      </div>

      {/* Hover-kaart (fixed, buiten modal container) */}
      {hoveredCard && (() => {
        const profile = hoveredCard.enrollment?.profiles;
        const naam = profile?.full_name || '?';
        const klantnummer = profile?.customer_number || '';
        const clientId = hoveredCard.clientId;
        const cached = !hoveredCard.loading
          ? hoverCacheRef.current[clientId]
          : null;
        const activeMembership = cached && cached !== 'loading' ? cached.membership : null;
        const hasCollectionsBlokkade = cached && cached !== 'loading' ? cached.has_collections : false;
        const endDate = calcEndDate(activeMembership);

        return (
          <div
            onMouseEnter={cancelHide}
            onMouseLeave={() => setHoveredCard(null)}
            style={{
              position: 'fixed',
              left: Math.max(8, Math.min(hoveredCard.x, window.innerWidth - 252)),
              top: hoveredCard.y,
              width: 240,
              background: '#fff',
              border: '1px solid var(--color-border)',
              borderRadius: 8,
              padding: 14,
              boxShadow: '0 4px 16px rgba(0,0,0,0.14)',
              zIndex: 1100,
              fontSize: 13,
              lineHeight: 1.5,
              pointerEvents: 'auto',
            }}
          >
            {hoveredCard.loading ? (
              <div style={{ display: 'flex', justifyContent: 'center', padding: '12px 0' }}>
                <div className="spinner" />
              </div>
            ) : (
              <>
                <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 1 }}>{naam}</div>
                {klantnummer && (
                  <div style={{ fontSize: 11, color: 'var(--color-text-muted)', marginBottom: 10 }}>
                    {klantnummer}
                  </div>
                )}
                <div style={{ borderTop: '1px solid var(--color-border)', paddingTop: 10 }}>
                  {activeMembership ? (
                    <>
                      <div style={{ marginBottom: 5 }}>
                        <span style={{ color: 'var(--color-text-muted)', fontSize: 12 }}>
                          {t('lesDetail.hoverLidmaatschap')}:{' '}
                        </span>
                        <span style={{ fontWeight: 500 }}>
                          {activeMembership.membership_types?.name || '—'}
                        </span>
                      </div>
                      <div style={{ marginBottom: 5 }}>
                        <span style={{ color: 'var(--color-text-muted)', fontSize: 12 }}>
                          {t('lesDetail.hoverStartdatum')}:{' '}
                        </span>
                        <span>
                          {activeMembership.start_date
                            ? format(new Date(activeMembership.start_date + 'T00:00:00'), 'd MMM yyyy', { locale: nl })
                            : '—'}
                        </span>
                      </div>
                      <div style={{ marginBottom: 10 }}>
                        <span style={{ color: 'var(--color-text-muted)', fontSize: 12 }}>
                          {t('lesDetail.hoverGeldigTm')}:{' '}
                        </span>
                        <span>
                          {endDate
                            ? format(new Date(endDate + 'T00:00:00'), 'd MMM yyyy', { locale: nl })
                            : '—'}
                        </span>
                      </div>
                    </>
                  ) : (
                    <div style={{ color: 'var(--color-text-muted)', marginBottom: 10, fontSize: 12 }}>
                      {t('lesDetail.hoverGeenLidmaatschap')}
                    </div>
                  )}
                  <div style={{ marginBottom: 10 }}>
                    {hasCollectionsBlokkade ? (
                      <span style={{
                        display: 'inline-block',
                        background: '#fee2e2', color: '#991b1b',
                        border: '1px solid #fca5a5',
                        fontSize: 11, fontWeight: 700,
                        padding: '2px 8px', borderRadius: 6,
                      }}>
                        {t('lesDetail.hoverBlokkade')}
                      </span>
                    ) : (
                      <span style={{
                        display: 'inline-block',
                        background: '#dcfce7', color: '#166534',
                        border: '1px solid #86efac',
                        fontSize: 11, fontWeight: 700,
                        padding: '2px 8px', borderRadius: 6,
                      }}>
                        {t('lesDetail.hoverActief')}
                      </span>
                    )}
                  </div>
                  <a
                    href={`/coach/clients/${clientId}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      color: 'var(--color-primary)', fontWeight: 500,
                      fontSize: 12, textDecoration: 'none',
                    }}
                  >
                    {t('lesDetail.hoverBekijkProfiel')}
                  </a>
                </div>
              </>
            )}
          </div>
        );
      })()}
    </div>
  );
}

function InfoRow({ label, value, danger }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
      <span>{label}</span>
      <span style={{ color: danger ? 'var(--color-danger)' : 'var(--color-text)', fontWeight: danger ? 700 : 400 }}>
        {value}
      </span>
    </div>
  );
}

function SectionHeader({ label, count, color }) {
  const bg = color || 'var(--color-primary)';
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 8,
      fontSize: 11, fontWeight: 700, color: 'var(--color-text-muted)',
      textTransform: 'uppercase', letterSpacing: '0.05em',
      marginBottom: 8,
    }}>
      {label}
      {count > 0 && (
        <span style={{
          background: bg, color: '#fff',
          borderRadius: 10, fontSize: 10, fontWeight: 700,
          padding: '1px 6px', lineHeight: 1.4,
        }}>
          {count}
        </span>
      )}
    </div>
  );
}

function EmptyRow({ text }) {
  return (
    <div style={{
      padding: '10px 12px', fontSize: 13,
      color: 'var(--color-text-muted)', marginBottom: 16,
    }}>
      {text}
    </div>
  );
}

function EnrolledRow({ enrollment, confirmId, unenrolling, onAskConfirm, onConfirm, onCancel, onRowEnter, onRowLeave, pushChecked, onPushCheckedChange, pushBericht, onPushBerichtChange }) {
  const profile = enrollment.profiles;
  const naam = profile?.full_name || '?';
  const klantnummer = profile?.customer_number || '';
  const isConfirming = confirmId === enrollment.id;

  return (
    <div
      onMouseEnter={e => onRowEnter(enrollment, e.currentTarget.getBoundingClientRect())}
      onMouseLeave={onRowLeave}
      style={{ padding: '9px 0', borderBottom: '1px solid var(--color-border)' }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <div style={{
          width: 32, height: 32, borderRadius: 16,
          background: 'var(--color-primary)22',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontWeight: 700, fontSize: 13, color: 'var(--color-primary)',
          flexShrink: 0,
        }}>
          {naam.charAt(0).toUpperCase()}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--color-primary)', display: 'flex', alignItems: 'center', gap: 6 }}>
            {naam}
            {enrollment.is_trial && (
              <span style={{
                background: 'var(--color-success)22',
                color: 'var(--color-success)',
                padding: '2px 6px',
                borderRadius: 4,
                fontSize: 10,
                fontWeight: 700,
                textTransform: 'uppercase',
              }}>
                {t('trialSession.badgeTrial')}
              </span>
            )}
          </div>
          {klantnummer && (
            <div style={{ fontSize: 11, color: 'var(--color-text-muted)' }}>{klantnummer}</div>
          )}
        </div>
        {!isConfirming && (
          <button
            onClick={() => onAskConfirm(enrollment)}
            style={{
              background: 'none', border: '1px solid var(--color-danger)',
              color: 'var(--color-danger)', borderRadius: 4,
              padding: '3px 8px', fontSize: 11, cursor: 'pointer',
              flexShrink: 0, fontWeight: 600,
            }}
          >
            {t('lesDetail.uitschrijven')}
          </button>
        )}
      </div>

      {isConfirming && (
        <div style={{
          marginTop: 8, padding: '10px 12px',
          background: 'var(--color-bg)', borderRadius: 6,
          border: '1px solid var(--color-border)',
        }}>
          <div style={{ fontSize: 12, color: 'var(--color-text-muted)', marginBottom: 8 }}>
            {t('lesDetail.uitschrijvenBevestiging').replace('{naam}', naam)}
          </div>
          <div style={{ display: 'flex', gap: 6, marginBottom: 10 }}>
            <button
              onClick={() => onConfirm(enrollment)}
              disabled={unenrolling}
              style={{
                background: 'var(--color-danger)', border: 'none', color: '#fff',
                borderRadius: 4, padding: '5px 12px', fontSize: 12, cursor: 'pointer', fontWeight: 600,
              }}
            >
              {unenrolling ? '…' : t('lesDetail.unenrollJa')}
            </button>
            <button
              onClick={onCancel}
              disabled={unenrolling}
              className="btn btn-ghost btn-sm"
              style={{ padding: '5px 12px', fontSize: 12 }}
            >
              {t('lesDetail.unenrollNee')}
            </button>
          </div>
          <label style={{
            display: 'flex', alignItems: 'center', gap: 8,
            cursor: 'pointer', fontSize: 12, marginBottom: 6, userSelect: 'none',
          }}>
            <input
              type="checkbox"
              checked={pushChecked}
              onChange={e => onPushCheckedChange(e.target.checked)}
              style={{ accentColor: 'var(--color-primary)', cursor: 'pointer' }}
            />
            {t('lesDetail.unenrollPushLabel')}
          </label>
          {pushChecked && (
            <textarea
              value={pushBericht}
              onChange={e => onPushBerichtChange(e.target.value)}
              rows={3}
              className="form-input"
              disabled={unenrolling}
              style={{ resize: 'vertical', fontFamily: 'inherit', fontSize: 12, width: '100%', boxSizing: 'border-box' }}
            />
          )}
        </div>
      )}
    </div>
  );
}

function WaitlistRow({ enrollment, position, onRowEnter, onRowLeave }) {
  const profile = enrollment.profiles;
  const naam = profile?.full_name || '?';
  const klantnummer = profile?.customer_number || '';

  return (
    <div
      onMouseEnter={e => onRowEnter(enrollment, e.currentTarget.getBoundingClientRect())}
      onMouseLeave={onRowLeave}
      style={{
        display: 'flex', alignItems: 'center', gap: 10,
        padding: '9px 0', borderBottom: '1px solid var(--color-border)',
      }}
    >
      <div style={{
        width: 32, height: 32, borderRadius: 16,
        background: '#f5a62322',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontWeight: 700, fontSize: 12, color: '#d97706',
        flexShrink: 0,
      }}>
        {position}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--color-primary)', display: 'flex', alignItems: 'center', gap: 6 }}>
          {naam}
          {enrollment.is_trial && (
            <span style={{
              background: 'var(--color-success)22',
              color: 'var(--color-success)',
              padding: '2px 6px',
              borderRadius: 4,
              fontSize: 10,
              fontWeight: 700,
              textTransform: 'uppercase',
            }}>
              {t('trialSession.badgeTrial')}
            </span>
          )}
        </div>
        {klantnummer && (
          <div style={{ fontSize: 11, color: 'var(--color-text-muted)' }}>{klantnummer}</div>
        )}
      </div>
      <div style={{ fontSize: 11, color: 'var(--color-text-muted)', flexShrink: 0 }}>
        {position}e {t('lesDetail.positie')}
      </div>
    </div>
  );
}

function PendingRow({ enrollment, onCancel, onRowEnter, onRowLeave }) {
  const profile = enrollment.profiles;
  const naam = profile?.full_name || '?';
  const klantnummer = profile?.customer_number || '';
  const clientId = enrollment.client_id;

  return (
    <div
      onMouseEnter={e => onRowEnter(enrollment, e.currentTarget.getBoundingClientRect())}
      onMouseLeave={onRowLeave}
      style={{
        display: 'flex', alignItems: 'center', gap: 10,
        padding: '9px 0', borderBottom: '1px solid var(--color-border)',
      }}
    >
      <div style={{
        width: 32, height: 32, borderRadius: 16,
        background: '#fef3c722',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontWeight: 700, fontSize: 13, color: '#d97706',
        flexShrink: 0,
      }}>
        ⏳
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--color-primary)', display: 'flex', alignItems: 'center', gap: 6 }}>
          {naam}
          {enrollment.is_trial && (
            <span style={{
              background: 'var(--color-success)22',
              color: 'var(--color-success)',
              padding: '2px 6px',
              borderRadius: 4,
              fontSize: 10,
              fontWeight: 700,
              textTransform: 'uppercase',
            }}>
              {t('trialSession.badgeTrial')}
            </span>
          )}
        </div>
        {klantnummer && (
          <div style={{ fontSize: 11, color: 'var(--color-text-muted)' }}>{klantnummer}</div>
        )}
        <a
          href={`/coach/clients/${clientId}`}
          target="_blank"
          rel="noopener noreferrer"
          style={{ fontSize: 11, color: 'var(--color-primary)', textDecoration: 'none' }}
        >
          {t('lesDetail.hoverBekijkProfiel')}
        </a>
      </div>
      <button
        onClick={() => onCancel(enrollment)}
        style={{
          background: 'none', border: '1px solid var(--color-danger)',
          color: 'var(--color-danger)', borderRadius: 4,
          padding: '3px 8px', fontSize: 11, cursor: 'pointer',
          flexShrink: 0, fontWeight: 600,
        }}
      >
        {t('lesDetail.pendingAnnuleren')}
      </button>
    </div>
  );
}
